import { Component, OnInit, OnDestroy } from '@angular/core';
import { Customer, CustomerService } from '../../services/customer';
import { CreateSalesOrderPayload, SalesOrderLine, SalesOrderService } from '../../services/sales-order';
import { NotificationService } from '../../services/notification';
import { PendingOrderService, PendingSalesOrder, SalesOrderState } from '../../services/pending-order-service';
import { Observable, Subscription, firstValueFrom, of } from 'rxjs';
import { PendingOrderSelectionService } from '../../services/pending-order-selection';
import { Product, ProductService, SelectedProduct, normalizeSaleItems, normalizeSalePrice } from '../../services/products';
import { CartComponent } from '../../components/cart-component/cart-component';
import { Router } from '@angular/router';
import { getPrice, PricelistService } from '../../services/price-lists';
import { CommonModule } from '@angular/common';
import { ProductListComponent } from '../../components/product-list-component/product-list-component';
import { MatButtonModule } from '@angular/material/button';
import { MessageDialogService } from '../../services/message-dialog';
import { CanComponentDeactivate } from '../../guards/navigation-prompt-guard';

@Component({
  selector: 'app-view-pending-order',
  templateUrl: './view-pending-order-component.html',
  styleUrls: ['./view-pending-order-component.scss'],
  standalone: true,
  imports: [
    CartComponent,
    CommonModule,
    ProductListComponent,
    MatButtonModule
  ]
})
export class ViewPendingOrderComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  selectedCustomer: Customer | null = null;
  selectedProducts: SelectedProduct[] = [];
  showProducts = false;
  disableEdit = true;
  products: Product[] = [];
  private subscription!: Subscription;
  private pendingOrder!: PendingSalesOrder;
  private isModified = false;

  constructor(
    private pendingOrderSelectionService: PendingOrderSelectionService,
    private customerService: CustomerService,
    private salesOrderService: SalesOrderService,
    private notificationService: NotificationService,
    private pendingOrderService: PendingOrderService,
    private productService: ProductService,
    private pricelistService: PricelistService,
    private router: Router,
    private messageDialog: MessageDialogService
  ) { }

  async ngOnInit(): Promise<void> {
    this.subscription = this.pendingOrderSelectionService.selectedItem$.subscribe(async order => {
      if (!order) return;

      this.pendingOrder = order;
      this.selectedProducts = order.salesOrder.order_line.map((line: SalesOrderLine) => ({
        productId: line.product_id,
        quantity: line.product_uom_qty,
        price: line.price_unit
      }));
      this.disableEdit = this.pendingOrder.state !== SalesOrderState.DRAFT;
      const customers = await firstValueFrom(this.customerService.get());
      this.selectedCustomer = customers.find(c => c.id === order.salesOrder.partner_id || (!!order.salesOrder.partner_client_uuid && c.clientCustomerUuid === order.salesOrder.partner_client_uuid)) ?? null;

      if (this.pendingOrder.state === SalesOrderState.SENT && this.pendingOrder.salesOrder.idempotencyKey) {
        this.salesOrderService.checkOrder({ idempotencyKey: this.pendingOrder.salesOrder.idempotencyKey }).subscribe({
          next: () => {
            // Order exists, do nothing
          },
          error: err => {
            if (err.status === 404) {
              // Order not found on server, allow editing
              this.pendingOrder.state = SalesOrderState.DRAFT;
              this.disableEdit = false;
              this.pendingOrderService.updatePendingOrder(this.pendingOrder).catch(console.error);
            } else {
              console.error('Error checking order:', err);
            }
          }
        });
      }

      this.productService.get().subscribe(products => {
        this.pricelistService.get().subscribe(pricelists => {
          const matching = pricelists.find(p => p.id === this.selectedCustomer?.priceListId);
          this.products = products.map(product => {
            return { ...product, list_price: normalizeSalePrice(product, getPrice(matching, product)) };
          });
        });
      });
    });
  }

  onCartUpdated(updatedCart: any[]) {
    this.selectedProducts = updatedCart;
    this.isModified = true;
  }

  async placeOrder(): Promise<void> {
    await this.customerService.syncPendingCustomers();
    if (this.pendingOrder.salesOrder.partner_id <= 0 && this.pendingOrder.salesOrder.partner_client_uuid) {
      const resolved = await this.customerService.findByClientUuid(this.pendingOrder.salesOrder.partner_client_uuid);
      if (!resolved || resolved.id <= 0 || resolved.pendingSync) {
        this.notificationService.showMessage('Customer is still pending sync.');
        return;
      }
      this.pendingOrder.salesOrder.partner_id = resolved.id;
      this.selectedCustomer = resolved;
      await this.pendingOrderService.updatePendingOrder(this.pendingOrder);
    }

    var payload = this.getPayLoad();
    if (!payload) return;
    this.pendingOrder.salesOrder = payload;

    this.salesOrderService.createOrder(this.pendingOrder.salesOrder).subscribe({
      next: async response => {
        if (response.status === 'success') {
          this.notificationService.showMessage('Order placed successfully');
          await this.pendingOrderService.removePendingOrder(this.pendingOrder.salesOrder.idempotencyKey);
          this.isModified = false;
          this.router.navigate(['/pending']);
        } else {
          this.notificationService.showMessage(response.message || 'Order creation failed');
        }
      },
      error: async err => {
        console.error('Retry failed:', err);
        const serverMessage = err?.error?.message || 'Still unable to place the order';
        if (err?.status === 400 && err?.error?.code === 'minimum_price_violation') {
          this.messageDialog.error('Product pricing changed. Sync products and try the order again.');
          return;
        }
        this.notificationService.showMessage(serverMessage);
      }
    });
  }

  editOrder(): void {
    this.showProducts = true;
  }

  async saveAsDraft(): Promise<void> {
    var payload = this.getPayLoad();
    if (!payload) return;
    this.pendingOrder.salesOrder = payload;
    await this.pendingOrderService.updatePendingOrder(this.pendingOrder);
    this.notificationService.showMessage('Order saved as draft');
    this.isModified = false;
    this.router.navigate(['/pending']);
  }

  deleteOrder(): void {
    this.messageDialog.confirm('Do you want to delete this order?', 'Confirm Deletion')
      .subscribe(async (confirmed: boolean) => {
        if (confirmed) {
          await this.pendingOrderService.removePendingOrder(this.pendingOrder.salesOrder.idempotencyKey);
          this.notificationService.showMessage('Removed order');
          this.isModified = false;
          this.router.navigate(['/pending']);
        }
      });
  }

  getPayLoad(): CreateSalesOrderPayload | false {
    if (!this.selectedCustomer) {
      this.messageDialog.error('No customer selected');
      return false;
    }

    const validProducts = this.selectedProducts.filter(p => p.quantity > 0);

    if (validProducts.length === 0) {
      this.messageDialog.error('Please select at least one product with quantity greater than zero.');
      return false;
    }

    const normalizedProducts = normalizeSaleItems(validProducts, this.products);

    const payload: CreateSalesOrderPayload = {
      ...this.pendingOrder.salesOrder, order_line: normalizedProducts.map(p => ({
        product_id: p.productId,
        product_uom_qty: p.quantity,
        price_unit: p.price
      }))
    };
    return payload
  }

  canDeactivate(): Observable<boolean> {
    if (!this.isModified) return of(true);
    return this.messageDialog.confirm(
      'You haven\'t saved the modified order. Leave this page?',
      'Confirm Navigation'
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.pendingOrderSelectionService.clear();
  }
}
